import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../services/api';
import Navbar from '../shared/Navbar';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

type PromptMessage = {
  role?: string;
  content?: any;
  [key: string]: any;
};

const messageContentToText = (content: any): string => {
  if (typeof content === 'string') return content;
  if (content == null) return '';
  try {
    return JSON.stringify(content, null, 2);
  } catch {
    return String(content);
  }
};

const PromptTesterPage: React.FC = () => {
  const location = useLocation();
  const incomingContext = (location.state as { context?: any } | null)?.context ?? null;
  const [me, setMe] = useState<{ authenticated: boolean; user?: { email: string; is_admin: boolean } } | null>(null);
  const [context, setContext] = useState<any | null>(incomingContext);
  const [paramsJson, setParamsJson] = useState<string>(() => {
    if (!incomingContext) return '';
    try {
      return JSON.stringify(incomingContext?.model?.parameters || {}, null, 2);
    } catch {
      return '';
    }
  });
  const [activeTab, setActiveTab] = useState<'context' | 'parameters'>('context');
  const [numSamples, setNumSamples] = useState<number>(3);
  const [generating, setGenerating] = useState<boolean>(false);
  const [results, setResults] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const messages = useMemo<PromptMessage[]>(
    () => (Array.isArray(context?.request_payload) ? context.request_payload : []),
    [context]
  );

  useEffect(() => {
    (async () => {
      try {
        const current = await api.me();
        setMe(current);
      } catch {
        setMe({ authenticated: false });
      }
    })();
  }, []);

  const onUploadContext = useCallback(async (file?: File) => {
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      setContext(data);
      setError(null);
      const p = data?.model?.parameters || {};
      try {
        setParamsJson(JSON.stringify(p, null, 2));
      } catch {
        setParamsJson('');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to parse context JSON');
    }
  }, []);

  const handleFilePick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = () => {
      const file = input.files && input.files[0];
      if (file) onUploadContext(file);
    };
    input.click();
  }, [onUploadContext]);

  const updateMessages = useCallback((nextMessages: PromptMessage[]) => {
    setContext((current: any) => ({
      ...(current || {}),
      request_payload: nextMessages
    }));
  }, []);

  const updateMessage = useCallback((index: number, field: 'role' | 'content', value: string) => {
    const nextMessages = messages.map((message, messageIndex) => (
      messageIndex === index ? { ...message, [field]: value } : message
    ));
    updateMessages(nextMessages);
  }, [messages, updateMessages]);

  const addMessage = useCallback(() => {
    updateMessages([...messages, { role: 'user', content: '' }]);
  }, [messages, updateMessages]);

  const removeMessage = useCallback((index: number) => {
    updateMessages(messages.filter((_, messageIndex) => messageIndex !== index));
  }, [messages, updateMessages]);

  const moveMessage = useCallback((index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= messages.length) return;
    const nextMessages = [...messages];
    [nextMessages[index], nextMessages[targetIndex]] = [nextMessages[targetIndex], nextMessages[index]];
    updateMessages(nextMessages);
  }, [messages, updateMessages]);

  const canGenerate = useMemo(() => !!context && numSamples > 0 && numSamples <= 20, [context, numSamples]);

  const generate = useCallback(async () => {
    if (!canGenerate) return;
    setGenerating(true);
    setResults(null);
    setError(null);
    try {
      let parameters: any = {};
      try {
        const trimmed = (paramsJson || '').trim();
        parameters = trimmed ? JSON.parse(trimmed) : {};
      } catch (e: any) {
        setError(`Invalid parameters JSON: ${e?.message || String(e)}`);
        setGenerating(false);
        return;
      }

      const payload = {
        context,
        overrides: {
          parameters
        },
        num_samples: numSamples
      };
      const res = await api.generatePromptSamples(payload);
      setResults(res?.samples || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to generate samples');
    } finally {
      setGenerating(false);
    }
  }, [canGenerate, context, paramsJson, numSamples]);

  const renderMathContent = (content: string) => (
    <ReactMarkdown remarkPlugins={[remarkMath, remarkGfm]} rehypePlugins={[rehypeKatex]}>
      {content || ''}
    </ReactMarkdown>
  );

  if (!(me?.authenticated && me?.user?.is_admin)) {
    return (
      <div className="app">
        <Navbar />
        <main className="app-main">
          <div className="content-container">
            <div className="page-intro"><h2>Prompt Tester</h2></div>
            <div className="admin-access-warning">Admin access required</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <Navbar />
      <main className="app-main">
        <div className="content-container">
          <div className="page-intro">
            <h2>Prompt Tester</h2>
            <p>Edit the model context and parameters, then generate samples</p>
          </div>
          <div className="prompt-tester-toolbar">
            <div className="prompt-tester-upload">
              <button className="tm-button tm-button--primary ml-replace-btn" onClick={handleFilePick}>📥 Upload Context JSON</button>
              {context ? <span className="ml-subtle">Context loaded</span> : null}
            </div>
            <div className="prompt-tester-run-controls">
              <label htmlFor="prompt-tester-num-samples">Samples</label>
              <input
                id="prompt-tester-num-samples"
                type="number"
                min="1"
                max="20"
                value={numSamples}
                onChange={e => setNumSamples(Math.max(1, Math.min(20, Number(e.target.value))))}
              />
              <button className="tm-button tm-button--primary ml-replace-btn" onClick={generate} disabled={!canGenerate || generating}>
                {generating ? 'Generating…' : '🧪 Generate Samples'}
              </button>
            </div>
          </div>
          {error ? <div className="ml-error prompt-tester-error">{error}</div> : null}

          <div className="prompt-tester-tabs" role="tablist" aria-label="Prompt tester configuration">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'context'}
              className={activeTab === 'context' ? 'active' : ''}
              onClick={() => setActiveTab('context')}
            >
              Context <span>{messages.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'parameters'}
              className={activeTab === 'parameters' ? 'active' : ''}
              onClick={() => setActiveTab('parameters')}
            >
              Parameters
            </button>
          </div>

          <div className="prompt-tester-tab-panel" role="tabpanel">
            {activeTab === 'context' ? (
              <section className="prompt-tester-messages">
                <div className="prompt-tester-section-header">
                  <div>
                    <h3>Context Messages</h3>
                    <p className="ml-subtle">These messages are sent to the model in the order shown.</p>
                  </div>
                  <button type="button" className="tm-button tm-button--primary ml-replace-btn" onClick={addMessage} disabled={!context}>
                    + Add Message
                  </button>
                </div>
                {!context ? (
                  <div className="ml-empty">Open a chat context or upload a context JSON file to edit its messages.</div>
                ) : messages.length === 0 ? (
                  <div className="ml-empty">This context has no messages. Add one to get started.</div>
                ) : (
                  <div className="prompt-message-list">
                    {messages.map((message, index) => {
                      const role = message.role || 'user';
                      const knownRoles = ['system', 'developer', 'user', 'assistant', 'tool'];
                      return (
                        <div className={`prompt-message-card role-${role}`} key={index}>
                          <div className="prompt-message-header">
                            <span className="prompt-message-number">#{index + 1}</span>
                            <select
                              aria-label={`Role for message ${index + 1}`}
                              value={role}
                              onChange={event => updateMessage(index, 'role', event.target.value)}
                            >
                              {!knownRoles.includes(role) && <option value={role}>{role}</option>}
                              {knownRoles.map(option => <option value={option} key={option}>{option}</option>)}
                            </select>
                            <div className="prompt-message-actions">
                              <button type="button" onClick={() => moveMessage(index, -1)} disabled={index === 0} title="Move up">↑</button>
                              <button type="button" onClick={() => moveMessage(index, 1)} disabled={index === messages.length - 1} title="Move down">↓</button>
                              <button type="button" className="prompt-message-delete" onClick={() => removeMessage(index)} title="Delete message">Delete</button>
                            </div>
                          </div>
                          <textarea
                            aria-label={`Content for message ${index + 1}`}
                            value={messageContentToText(message.content)}
                            onChange={event => updateMessage(index, 'content', event.target.value)}
                            rows={Math.max(3, Math.min(12, messageContentToText(message.content).split('\n').length + 1))}
                            placeholder={`${role} message content`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            ) : (
              <section className="prompt-tester-parameters">
                <div className="prompt-tester-section-header">
                  <div>
                    <h3>Model Parameters</h3>
                    <p className="ml-subtle">Override the model configuration using JSON.</p>
                  </div>
                </div>
                <textarea
                  value={paramsJson}
                  onChange={e => setParamsJson(e.target.value)}
                  placeholder='{"model":"provider/model","max_sections_in_context":3,"temperature":0.7,"top_p":1}'
                  rows={18}
                  spellCheck={false}
                />
              </section>
            )}
          </div>

          <div className="tm-mt-lg">
            <h3>Results</h3>
            {!results ? (
              <div className="ml-subtle">No samples yet.</div>
            ) : (
              <div className="tm-stack">
                {results.map((r, idx) => (
                  <div key={idx} className="ml-item" style={{ padding: 12 }}>
                    <div className="tm-between">
                      <div className="tm-semibold">Sample #{r.index ?? idx}</div>
                      {r.error ? <span className="ml-error">{r.error}</span> : null}
                    </div>
                    <div className="tm-mt-sm">
                      {renderMathContent(r.content || '')}
                    </div>
                    {Array.isArray(r.tool_calls) && r.tool_calls.length > 0 ? (
                      <div className="tm-mt-sm">
                        <div className="tm-semibold">Tool Calls</div>
                        <pre className="tm-scroll-x">{JSON.stringify(r.tool_calls, null, 2)}</pre>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PromptTesterPage;
